/* eslint-disable react/no-array-index-key */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OfferingsModel {
    fields: {
        addVideo: boolean;
        addImageTitle: boolean;
        title: string;
        image: string;
        destination: LinkFieldValue;
        linkTitle: string;
        hoveredTitle: string;
        hoveredSubtitle: string;
        hoveredDescription: string;
        hoveredDestination: LinkFieldValue;
        hoveredLinkTitle1: string;
        hoveredLinkTitle2: string;
        videoTitle: string;
        videoUrl: string;
        videoTime: string;
    };
}

const enum fields {
    offerings = "offerings",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.offerings]: OfferingsModel[];
    };
    extendedStyles?: OfferingStyles;
}

export interface OfferingStyles {
    link?: LinkProps;
    image?: LazyImageProps;
    titleText?: TypographyProps;
    offeringTitle: TypographyProps;
    gridWrapper?: GridContainerProps;
    bannerTextGridItem?: GridItemProps;
    hoveredGridItem?: GridItemProps;
    videoGridItem?: GridItemProps;
    descriptionWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    seperator?: InjectableCss;
    linkSeperator?: InjectableCss;
    offeringSubTitle?: InjectableCss;
    description?: InjectableCss;
    hoveredCardink?: InjectableCss;
    hoveredCardinkWrapper?: InjectableCss;
    hoveredCardTitle?: InjectableCss;
    videoTitle?: InjectableCss;
    mobileDefaultIconWrapper?: InjectableCss;
    mobileCloseIconWrapper?: InjectableCss;
    mobileHoverWrapper?: InjectableCss;
}

export const offeringStyles: OfferingStyles = {
    titleText: {
        variant: "h3",
        transform: "uppercase",
        css: css`
            border-width: 20px;
            text-align: center;
            margin: 0;
            padding: 30px 0;
        `,
    },
    link: {
        css: css`
            display: flex;
            position: relative;
            color: ${COLORS.link.base};
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-bottom: 10px;
        `,
    },
    offeringTitle: {
        css: css`
            margin: 0;
            width: 100%;
        `,
    },
    gridWrapper: {
        gap: 24,
        css: css`
            flex-wrap: wrap;
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                    [
                        css`
                        margin-bottom: 56px;
                        `,
                        css`
                        margin-bottom: 78px;
                        `,
                        null,
                        null,
                    ],
            )}
        `,
    },
    bannerTextGridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
        flex-shrink: 1 !important;
        flex-grow: 1 !important;
        > .hoveredCard {
            display: none;
            transition: all .2s ease-in-out;
        }
        @media (min-width: 768px) {
            &:hover {
                > div:not(.hoveredCard) {
                    display: none;

                    + .hoveredCard {
                        display: block;
                        flex-shrink: 1 !important;
                        flex-grow: 1 !important;
                        transform: scale(1.05);
                        box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                        background: #f2f2f2;
                    }
                }
            }
        }
        ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
                [
                    css`
                    `,
                    css`
                    &.hoverEffect > .hoveredCard {
                        display: block;
                        transform: scale(1.05);
                        box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
                    }
                    &.hoverEffect > div:not(.hoveredCard) {
                        display: none;
                    }
                    `,
                    null,
                    null,
                    ],
                "max",
        )}
        `,
    },
    hoveredGridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
        `,
    },
    videoGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
        flex-direction: column;
        background: ${COLORS.white};
        @media (min-width: 992px) {
            &:hover {
                transition: all .2s ease-in-out;
                transform: scale(1.05);
                box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            }
        }
        .defaultMobileIcon {
            display: block;
        }
        .hoverMobileIcon {
            display: none;
        }
        &.video-wrapper.hoverEffect {
            transition: all .2s ease-in-out;
            transform: scale(1.05);
            box-shadow: 0px 8px 16px rgba(38, 37, 37, 0.24);
            .defaultMobileIcon {
                display: none;
            }
            .hoverMobileIcon {
                display: block;
            }
        }
        `,
    },
    descriptionWrapper: {
        css: css`
            padding: 32px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            background: ${COLORS.background.grayShade};
            @media (min-width: 992px) and (max-width: 1159px) {
                padding: 20px;
            }
        `,
    },
    cardWrapper: {
        css: css`
            background: ${COLORS.white};
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            width: 100%;
            height: 100%;
            max-height: 328px;
        `,
    },
    image: {
        css: css`
            height: auto;
            img {
                width: 100%;
            }
        `,
    },
    mobileDefaultIconWrapper: {
        css: css`
            display: none;
            background: #f2f2f2;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                    [
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        null,
                        null,
                        ],
                    "max",
            )}
        `,
    },
    mobileCloseIconWrapper: {
        css: css`
            display: none;
            background: #f2f2f2;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                    [
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        css`
                            display: flex;
                            justify-content: flex-end;
                            padding-right: 12px;
                            padding-bottom: 12px;
                        `,
                        null,
                        null,
                        ],
                    "max",
            )}
        `,
    },
    seperator: {
        css: css`
            display: inline-block;
            height: 11px;
            width: 2px;
            background: ${getColor("common.borderLight")};
            margin: 0 5px;
        `,
    },
    linkSeperator: {
        css: css`
            display: inline-block;
            height: 11px;
            width: 2px;
            background: #807F80;
            margin: 0 5px;
        `,
    },
    offeringSubTitle: {
        css: css`
            margin: 0;
            width: 100%;
            padding-bottom: 10px;
            font-size: ${TYPOGRAPHY.size.tiny};
            color: ${COLORS.text.lighter};
        `,
    },
    description: {
        css: css`
            flex-grow: 1;
            margin-bottom: 20px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 24px;
            display: -webkit-box;
            -webkit-line-clamp: 6;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            @media (min-width: 992px) and (max-width: 1159px) {
                -webkit-line-clamp: 5;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            }
        `,
    },
    hoveredCardinkWrapper: {
        css: css`
            display: flex;
        `,
    },
    hoveredCardink: {
        css: css`
            display: flex;
            position: relative;
            color: ${COLORS.link.base};
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            &:hover {
                color: #283E49;
            }
            @media (min-width: 992px) and (max-width: 1159px) {
                font-size: 12px;
            }
        `,
    },
    hoveredCardTitle: {
        css: css`
            margin: 0;
            width: 100%;
            padding-bottom: 10px;
        `,
    },
    videoTitle: {
        css: css`
            margin-top: 10px;
        `,
    },
    mobileHoverWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                    [
                        null,
                        css`
                        display: flex;
                        flex-direction: row;
                        width: 100%;
                        > div {
                            flex: 1;
                            padding: 10px 12px;
                            background: transparent;
                        }
                        `,
                        null,
                        null,
                        ],
                    "max",
            )}
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        pageLink: ownProps.fields.offerings.map(card => getLink(state, card.fields.destination)),
        hoveredPageLink: ownProps.fields.offerings.map(hoveredCard => getLink(state, hoveredCard.fields.hoveredDestination)),
    };
};

const mapDispatchToProps = {};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;


const WhyCentroWidget: React.FC<Props> = ({ fields, pageLink, hoveredPageLink, extendedStyles }) => {
    const [styles] = useState(() => mergeToNew(offeringStyles, extendedStyles));
    const [isActive, setState] = React.useState(false);

    const addClassMobileHover = (e: any) => {
        const boxes = document.querySelectorAll(`.mobileRemove:not(#${e.currentTarget.id})`);
        for (const box of boxes) {
            box.classList.remove("hoverEffect");
        }
        e.currentTarget.classList.toggle("hoverEffect");
    };

    return (
        <GridContainer {...styles.gridWrapper}>
            {fields.offerings &&
                    fields.offerings.map((offering, index) => (
                        <>
                        {offering.fields.addImageTitle &&
                        <GridItem {...styles.bannerTextGridItem} key={index} onClick={(e) => addClassMobileHover(e)} id={`mobileContainer${index}`} className="mobileRemove">
                            <StyledWrapper {...styles.cardWrapper}>
                                <LazyImage {...styles.image} src={offering.fields.image} altText={offering.fields.title} />
                                <StyledWrapper {...styles.descriptionWrapper}>
                                    <Clickable href={pageLink[index]?.url} {...styles.link} target="_blank">
                                        {translate("{0}", offering.fields.linkTitle.toString())}
                                    </Clickable>
                                    <Typography variant="h5" {...styles.offeringTitle}>{translate("{0}",offering.fields.title)}</Typography>
                                    
                                </StyledWrapper>
                                <StyledWrapper data-test-selector="mobile-hover-icons" {...styles.mobileDefaultIconWrapper}>
                                        <LazyImage src="/userFiles/images/mobile-i-icon.png" altText="mobile icon" className="defaultMobileIcon" />
                                    </StyledWrapper>
                            </StyledWrapper>

                            <StyledWrapper {...styles.cardWrapper} className="hoveredCard">
                            <StyledWrapper {...styles.descriptionWrapper}>
                                <Typography variant="legend" {...styles.offeringSubTitle}>
                                    {translate("{0}",offering.fields.hoveredSubtitle)}</Typography>
                                <Typography variant="h5" {...styles.hoveredCardTitle}>{translate("{0}",offering.fields.title)}</Typography>
                                <Typography {...styles.description}>
                                    {translate("{0}",offering.fields.hoveredDescription)}
                                </Typography>
                                <StyledWrapper {...styles.hoveredCardinkWrapper}>
                                    <Clickable href={pageLink[index]?.url} {...styles.hoveredCardink} target="_blank">
                                        {translate("{0}",offering.fields.hoveredLinkTitle1)}
                                    </Clickable>
                                    <Clickable href={hoveredPageLink[index]?.url} {...styles.hoveredCardink} target="_blank">
                                        <Typography {...styles.linkSeperator}></Typography> 
                                        {translate("{0}",offering.fields.hoveredLinkTitle2)}
                                    </Clickable>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper data-test-selector="mobile-hover-icons" {...styles.mobileCloseIconWrapper}>
                                    <LazyImage src="/userFiles/images/mobile-close-icon.png" altText="mobile icon" className="hoverMobileIcon"/>
                                </StyledWrapper>
                        </StyledWrapper>
                        </GridItem>
                        }
                        {offering.fields.addVideo &&
                        <GridItem id={`mobileContainer${index}`} onClick={(e) => addClassMobileHover(e)} className="video-wrapper mobileRemove" {...styles.videoGridItem}>
                            <iframe width="100%" height="300px" src={offering.fields.videoUrl} frameBorder="0"></iframe>
                            <StyledWrapper {...styles.mobileHoverWrapper}>
                            <Typography variant="legend" {...styles.videoTitle}>{translate("{0}",offering.fields.videoTitle)} 
                                <Typography {...styles.seperator}></Typography> 
                            {translate("{0}",offering.fields.videoTime)}</Typography> 
                            <StyledWrapper data-test-selector="mobile-hover-icons" {...styles.mobileDefaultIconWrapper}>
                                        <LazyImage src="/userFiles/images/mobile-i-icon.png" altText="mobile icon" className="defaultMobileIcon" />
                                        <LazyImage src="/userFiles/images/mobile-close-icon.png" altText="mobile icon" className="hoverMobileIcon"/>
                                    </StyledWrapper>
                            </StyledWrapper>
                            
                        </GridItem>
                        }
                        
                        </>
                ))}
                
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WhyCentroWidget),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.offerings,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "addImageTitle",
                        displayName: "Add Image Title card",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "addVideo",
                        displayName: "Add video",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredSubtitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredDescription",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredLinkTitle1",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredLinkTitle2",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "hoveredDestination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: widget => widget.fields.addImageTitle === true,
                    },
                    {
                        name: "videoTitle",
                        editorTemplate: "TextField",
                        displayName: "Video Title",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addVideo === true,
                    },
                    {
                        name: "videoTime",
                        editorTemplate: "TextField",
                        displayName: "Video Time",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addVideo === true,
                    },
                    {
                        name: "videoUrl",
                        editorTemplate: "TextField",
                        displayName: "Video Link",
                        defaultValue: "",
                        isVisible: widget => widget.fields.addVideo === true,
                    },
                ],
            },
            
        ],
    },
};

export default widgetModule;
